<template>
  <div class="tm-reservation-modal" @click.self="$emit('close')">
    <div class="tm-reservation-modal__content">
      <button class="tm-reservation-modal__close" @click="$emit('close')">
        <i class="icon icon--close-btn"></i>
      </button>
      <h3 class="tm-reservation-modal__title">
        {{ editItem !== null ? $t('reporting.timeMachine.reservationModal.editTitle') : $t('reporting.timeMachine.reservationModal.title') }}
      </h3>
      <div class="tm-reservation-modal__form">
        <div class="tm-reservation-modal__field">
          <label class="tm-reservation-modal__field__label">
            {{ $t('reporting.timeMachine.reservationModal.startDate') }}
          </label>
          <div class="tm-reservation-modal__field__date-wrap">
            <div class="tm-reservation-modal__field__date" @click.stop="toggleDatePicker">
              <i class="icon icon--time-machine-calendar"></i>
              <span>{{ formattedDate }}</span>
            </div>
            <date-picker
              v-if="showDatePicker"
              :min-date="minDate"
              @input="onDateSelect"
              @onCloseClick="showDatePicker = false"
            />
          </div>
        </div>
        <div class="tm-reservation-modal__field">
          <label class="tm-reservation-modal__field__label">
            {{ $t('reporting.timeMachine.reservationModal.startTime') }}
          </label>
          <div class="tm-reservation-modal__field__time">
            <div class="tm-dropdown" @click.stop="toggleDropdown('hour')">
              <span class="tm-dropdown__value">
                {{ hour }}
                <i class="icon icon--time-machine-time-chevron"></i>
              </span>
              <transition name="slide">
                <ul v-show="openDropdown === 'hour'" class="tm-dropdown__list">
                  <li v-for="h in hourOptions" :key="h"
                      :class="{ active: h === hour }"
                      @click.stop="selectOption('hour', h)">{{ h }}</li>
                </ul>
              </transition>
            </div>
            <span class="time-separator">:</span>
            <div class="tm-dropdown" @click.stop="toggleDropdown('minute')">
              <span class="tm-dropdown__value">
                {{ minute }}
                <i class="icon icon--time-machine-time-chevron"></i>
              </span>
              <transition name="slide">
                <ul v-show="openDropdown === 'minute'" class="tm-dropdown__list">
                  <li v-for="m in minuteOptions" :key="m"
                      :class="{ active: m === minute }"
                      @click.stop="selectOption('minute', m)">{{ m }}</li>
                </ul>
              </transition>
            </div>
          </div>
        </div>
        <div class="tm-reservation-modal__field">
          <label class="tm-reservation-modal__field__label">
            {{ $t('reporting.timeMachine.reservationModal.duration') }}
          </label>
          <div class="tm-dropdown" @click.stop="toggleDropdown('duration')">
            <span class="tm-dropdown__value">{{ selectedDurationLabel }}</span>
            <i class="icon icon--time-machine-select-down"></i>
            <transition name="slide">
              <ul v-show="openDropdown === 'duration'" class="tm-dropdown__list">
                <li v-for="opt in durationOptions" :key="opt.value"
                    :class="{ active: opt.value === duration }"
                    @click.stop="selectOption('duration', opt.value)">{{ opt.label }}</li>
              </ul>
            </transition>
          </div>
        </div>
      </div>
      <ul class="tm-reservation-modal__rules">
        <li v-for="(rule, idx) in 4" :key="idx">
          {{ $t(`reporting.timeMachine.reservationModal.rules[${idx}]`) }}
          <span v-if="needsCredit && idx === 0" class="tm-reservation-modal__rules__credit">
            {{ $t('reporting.timeMachine.reservationModal.rulesCredit') }}
          </span>
        </li>
      </ul>
      <div class="tm-reservation-modal__divider"></div>
      <div class="tm-reservation-modal__actions">
        <ui-button @click="$emit('close')">
          {{ $t('reporting.timeMachine.reservationModal.cancel') }}
        </ui-button>
        <ui-button primary @click="onConfirm">
          {{ $t('reporting.timeMachine.reservationModal.confirm') }}
        </ui-button>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { utcToLocalTimezone } from '@/libs/utils'
import UiButton from '@/components/ui/UiButton'
import DatePicker from '@/components/date-picker'

export default {
  name: 'TimeMachineReservationModal',
  components: {
    UiButton,
    DatePicker
  },
  props: {
    editItem: {
      type: Object,
      default: null
    },
    reservationDraft: {
      type: Object,
      default: null
    },
    timezoneName: {
      type: String,
      default: ''
    },
    timezone: {
      type: String,
      default: ''
    },
    needsCredit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      startDate: this.timezoneName ? utcToLocalTimezone(new Date(), this.timezoneName) : new Date(),
      hour: '00',
      minute: '00',
      duration: 5,
      openDropdown: null,
      showDatePicker: false
    }
  },
  created () {
    this.syncWithSourceItem(this.editItem || this.reservationDraft)
  },
  computed: {
    tzNow () {
      return this.timezoneName ? utcToLocalTimezone(new Date(), this.timezoneName) : new Date()
    },
    formattedDate () {
      return format(this.startDate, 'MMM DD, YYYY')
    },
    minDate () {
      return this.tzNow
    },
    isToday () {
      return this.startDate.toDateString() === this.tzNow.toDateString()
    },
    crossesMidnight () {
      if (!this.isToday) return false
      const minTime = new Date(this.tzNow.getTime() + 30 * 60 * 1000)
      return minTime.getDate() !== this.tzNow.getDate()
    },
    hourOptions () {
      const options = []
      if (this.isToday) {
        const minTime = new Date(this.tzNow.getTime() + 30 * 60 * 1000)
        const minHour = minTime.getHours()
        for (let h = minHour; h <= 23; h++) {
          options.push(String(h).padStart(2, '0'))
        }
      } else {
        for (let i = 0; i <= 23; i++) {
          options.push(String(i).padStart(2, '0'))
        }
      }
      return options
    },
    minuteOptions () {
      const options = []
      if (this.isToday) {
        const minTime = new Date(this.tzNow.getTime() + 30 * 60 * 1000)
        const minHour = minTime.getHours()
        const minMinute = minTime.getMinutes()
        for (let m = 0; m < 60; m += 5) {
          if (Number(this.hour) === minHour && m < minMinute) {
            continue
          }
          options.push(String(m).padStart(2, '0'))
        }
      } else {
        for (let j = 0; j < 60; j += 5) {
          options.push(String(j).padStart(2, '0'))
        }
      }
      return options
    },
    durationOptions () {
      const unit = this.$t('reporting.timeMachine.minutes')
      return [5, 10, 15, 20, 25, 30].map(v => ({
        value: v,
        label: `${v}${unit}`
      }))
    },
    selectedDurationLabel () {
      const opt = this.durationOptions.find(o => o.value === this.duration)
      return opt ? opt.label : ''
    }
  },
  watch: {
    editItem: {
      handler (value) {
        this.syncWithSourceItem(value)
      },
      deep: true
    },
    reservationDraft: {
      handler (value) {
        this.syncWithSourceItem(value)
      },
      deep: true
    },
    crossesMidnight: {
      handler (val) {
        if (val) {
          const tomorrow = new Date(this.tzNow)
          tomorrow.setDate(tomorrow.getDate() + 1)
          tomorrow.setHours(0, 0, 0, 0)
          this.startDate = tomorrow
        }
      },
      immediate: true
    },
    hourOptions: {
      handler (options) {
        if (!options.includes(this.hour)) {
          this.hour = options[0] || '00'
        }
      },
      immediate: true
    },
    minuteOptions: {
      handler (options) {
        if (!options.includes(this.minute)) {
          this.minute = options[0] || '00'
        }
      },
      immediate: true
    }
  },
  mounted () {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    syncWithSourceItem (item) {
      if (!item || !item.startDate) return
      const startDateValue = String(item.startDate)
      const normalizedStartDate = startDateValue.endsWith('Z') ? startDateValue : `${startDateValue}Z`
      const parsedStartDate = new Date(normalizedStartDate)
      if (isNaN(parsedStartDate.getTime())) return
      const targetDate = this.timezoneName ? utcToLocalTimezone(parsedStartDate, this.timezoneName) : parsedStartDate
      this.startDate = targetDate
      this.hour = String(item.hour || targetDate.getHours()).padStart(2, '0')
      this.minute = String(item.minute || targetDate.getMinutes()).padStart(2, '0')
      this.duration = item.duration || this.duration
    },
    toggleDatePicker () {
      this.showDatePicker = !this.showDatePicker
    },
    onDateSelect (dateStr) {
      this.startDate = new Date(dateStr)
      this.showDatePicker = false
    },
    toggleDropdown (name) {
      this.openDropdown = this.openDropdown === name ? null : name
    },
    selectOption (name, value) {
      this[name] = value
      this.openDropdown = null
    },
    handleOutsideClick (e) {
      this.openDropdown = null
      const datePicker = this.$el.querySelector('.date_picker')
      if (this.showDatePicker && (!datePicker || !datePicker.contains(e.target))) {
        this.showDatePicker = false
      }
    },
    onConfirm () {
      this.$emit('confirm', {
        startDate: this.startDate,
        hour: this.hour,
        minute: this.minute,
        duration: this.duration,
        timezoneName: this.timezoneName,
        timezone: this.timezone
      })
    }
  }
}
</script>

<style scoped lang="scss">
.tm-reservation-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  div {
    box-sizing: border-box;
  }

  .tm-dropdown {
    position: relative;
    display: inline-flex;
    align-items: center;
    height: 38px;
    min-width: 144px;
    padding: 9px 10px;
    border-radius: 5px;
    border: 1px solid #BBB;
    background: #FFF;
    cursor: pointer;

    &:hover {
      background: #F4F4F4;
    }

    &__value {
      color: #2F2F2F;
      font-size: 14px;
      font-weight: 400;
      line-height: 100%;
    }

    .icon {
      position: absolute;
      right: 10px;
      pointer-events: none;
    }

    &__list {
      position: absolute;
      top: -1px;
      left: -1px;
      min-width: calc(100% + 2px);
      max-height: 200px;
      overflow-y: auto;
      margin: 0;
      padding: 0;
      padding-inline-start: 0;
      list-style: none;
      border-radius: 5px;
      border: 1px solid #E0E0E0;
      background: #FFF;
      box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
      z-index: 10;
      transform-origin: top;
      transition: transform .2s ease, opacity .1s ease;

      li {
        padding: 11px 10px;
        color: #2F2F2F;
        font-size: 14px;
        font-weight: 400;
        line-height: 100%;
        cursor: pointer;
        height: 36px;
        box-sizing: border-box;

        &:first-child {
          border-radius: 5px 5px 0 0;
        }

        &:last-child {
          border-radius: 0 0 5px 5px;
        }

        &:hover {
          background: #F4F4F4;
        }

        &.active {
          background: #F4F4F4;
          font-weight: 600;
        }
      }
    }
  }

  &__content {
    position: relative;
    width: 560px;
    background: #FFF;
    border-radius: 16px;
    padding: 24px 28px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  }

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    cursor: pointer;
  }

  &__title {
    color: #2F2F2F;
    font-size: 18px;
    font-weight: 600;
    line-height: 18px;
    margin-top: 4px;
    margin-bottom: 36px;
  }

  &__form {
    border-radius: 5px;
    border: 1px solid #D7D7D7;
    background: #F4F4F4;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__field {
    display: flex;
    align-items: center;

    &__label {
      min-width: 80px;
      color: #666;
      font-size: 14px;
      font-weight: 500;
      line-height: 150%;
    }

    &__date-wrap {
      position: relative;

      .date_picker {
        position: absolute;
        top: calc(100% + 4px);
        left: 110%;
        z-index: 20;
      }
    }

    &__date {
      display: flex;
      align-items: center;
      min-width: 144px;
      gap: 8px;
      height: 44px;
      padding: 9px;
      border-radius: 5px;
      border: 1px solid #BBB;
      background: #FFF;
      cursor: pointer;
      color: #666;
      font-size: 14px;
      font-weight: 400;
      line-height: 100%;

      &:hover {
        border-color: #BBB;
      }
    }

    &__time {
      display: flex;
      align-items: center;
      gap: 8px;

      .time-separator {
        color: #2F2F2F;
        font-size: 14px;
        font-weight: 400;
        line-height: 150%;
      }

      .tm-dropdown {
        min-width: 62px;
        width: 62px;
        justify-content: space-between;
        padding: 9px 6px 9px 11px;
      }
    }
  }

  &__rules {
    margin-top: 12px;
    padding: 12px;
    list-style: none;

    li {
      position: relative;
      padding-left: 12px;
      color: #888;
      font-size: 13px;
      font-weight: 400;
      line-height: 24px;

      &::before {
        content: '▪';
        position: absolute;
        left: 0;
        color: #888;
      }
    }

    &__credit {
      display: block;
      padding-left: 0;
      color: #EC0047;
      font-size: 13px;
      font-weight: 400;
      line-height: 24px;
    }
  }

  &__divider {
    margin-top: 12px;
    border-top: 1px solid #E0E0E0;
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: 4px;
    margin-top: 24px;

    .ui-btn {
      width: 90px;
      border-radius: 999px;
    }
  }
}

.slide-enter,
.slide-leave-to {
  transform: scaleY(0);
  opacity: 0;
}
</style>
